// Client-side notification scheduling. We use the browser Notification API
// (no service-worker push setup required) and persist timers in localStorage
// so reminders survive page reloads — re-evaluated on every app open.
import storage from "./storage";
import { track, EVENTS } from "./analytics";

const KEY = "sm_notif_schedule";   // { kind: { fireAt, payload } }

function read() {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}") || {}; } catch { return {}; }
}
function write(obj) {
  try { localStorage.setItem(KEY, JSON.stringify(obj || {})); } catch { /* quota */ }
}

export function hasPermission() {
  return typeof Notification !== "undefined" && Notification.permission === "granted";
}

export function hasAsked() {
  return typeof Notification !== "undefined" && Notification.permission !== "default";
}

export async function requestPermission() {
  if (typeof Notification === "undefined") return "denied";
  const result = await Notification.requestPermission();
  if (result === "granted") track(EVENTS.NOTIFICATION_PERMISSION_GRANTED);
  else track(EVENTS.NOTIFICATION_PERMISSION_DENIED);
  return result;
}

// Show now (used both as a fallback when scheduler fires and as a manual ping).
function show(title, body, route) {
  if (!hasPermission()) return;
  try {
    const n = new Notification(title, {
      body, icon: "/icon.svg", badge: "/icon.svg", tag: route || title,
      data: { route },
    });
    n.onclick = () => {
      try { window.focus(); } catch { /* */ }
      track(EVENTS.NOTIFICATION_CLICKED, { route });
      if (route) window.location.href = route;
      n.close();
    };
  } catch (_) { /* */ }
}

// ---- 5 scheduled notifications --------------------------------------------
// 1. After any scan: ask in 6h if the product caused a reaction.
// 2. After 2 scans (not yet 3): "you're one scan away".
// 3. After 3 reactions: "we found something" — immediately.
// 4. Every Friday at 10am: weekend reminder.
// 5. After 7 days inactivity: "your skin memory is waiting".

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export function scheduleAfterScan(productName) {
  if (!hasPermission()) return;
  const sched = read();
  sched.after_scan = {
    fireAt: Date.now() + SIX_HOURS_MS,
    title: `How did ${productName || "that product"} feel?`,
    body: "Tap to log any reaction so we can find your pattern.",
    route: "/scan",
  };
  write(sched);
}

export function scheduleScanReminder(scanCount) {
  if (!hasPermission()) return;
  if (scanCount !== 2) return;
  const sched = read();
  sched.scan_two = {
    fireAt: Date.now() + 30 * 60 * 1000,  // 30-min gentle nudge
    title: "You're one scan away from your pattern",
    body: "One more product and we can start matching triggers.",
    route: "/scan",
  };
  write(sched);
}

export function firePatternFound() {
  if (!hasPermission()) return;
  show(
    "We found something about your skin 🌿",
    "Tap to see your top suspect ingredient.",
    "/insight",
  );
}

export function scheduleFridayReminder() {
  if (!hasPermission()) return;
  const sched = read();
  // Next Friday at 10:00 local.
  const now = new Date();
  const target = new Date(now);
  const daysUntilFri = (5 - now.getDay() + 7) % 7 || 7;
  target.setDate(now.getDate() + daysUntilFri);
  target.setHours(10, 0, 0, 0);
  if (target.getTime() <= now.getTime()) target.setDate(target.getDate() + 7);
  sched.friday = {
    fireAt: target.getTime(),
    title: "Shopping this weekend?",
    body: "Check your products in SkinMemory before buying.",
    route: "/scan",
    repeating: true,
  };
  write(sched);
}

export function scheduleInactivityReminder() {
  if (!hasPermission()) return;
  const sched = read();
  sched.inactive_7d = {
    fireAt: Date.now() + SEVEN_DAYS_MS,
    title: "Your skin memory is waiting 🌿",
    body: "A few seconds of logging keeps the pattern sharp.",
    route: "/",
  };
  write(sched);
}

// ---- Tick loop -------------------------------------------------------------
// Re-evaluates the queue on every app open + every 60s. setTimeout doesn't
// survive tab close on most mobile browsers, so we always trust the
// persisted fireAt timestamp.
let _ticker = null;
export function startScheduler() {
  if (_ticker) return;
  const tick = () => {
    if (!hasPermission()) return;
    const sched = read();
    const now = Date.now();
    let changed = false;
    for (const [k, v] of Object.entries(sched)) {
      if (v?.fireAt && v.fireAt <= now) {
        show(v.title, v.body, v.route);
        if (v.repeating && k === "friday") {
          // Reschedule next week.
          v.fireAt = v.fireAt + 7 * 24 * 60 * 60 * 1000;
        } else {
          delete sched[k];
        }
        changed = true;
      }
    }
    if (changed) write(sched);

    // Inactivity is rolling: bump fireAt forward on every app open.
    if (storage.getEffectiveUserId) {
      const s = read();
      s.inactive_7d = {
        fireAt: now + SEVEN_DAYS_MS,
        title: "Your skin memory is waiting 🌿",
        body: "A few seconds of logging keeps the pattern sharp.",
        route: "/",
      };
      write(s);
    }
  };
  tick();
  _ticker = setInterval(tick, 60 * 1000);
}

export function clearAllSchedules() {
  write({});
}

export default {
  hasPermission, hasAsked, requestPermission,
  scheduleAfterScan, scheduleScanReminder, firePatternFound,
  scheduleFridayReminder, scheduleInactivityReminder,
  startScheduler, clearAllSchedules,
};
